import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import GLDeterminationTable from '../components/itemRequests/GLDeterminationTable';
import apiService from '../services/api.service';
import { showSnackbar } from '../store/slices/uiSlice';
import {
  FIELD_LABELS,
  ITEM_REQUEST_STATUS,
  keptWarehouseKeys,
  type GLAccounts,
  type GLOverride,
  type GLPreview,
  type GLRow,
  type ItemRequestDetail as Detail,
  type ItemRequestLookups,
} from '../types/itemRequests';

/**
 * One new item request: its fields, the G/L accounts SAP will give the item,
 * and the step actions. The Finance reviewer can set accounts for this item
 * only; the Procurement Manager's approval creates the item in SAP.
 */

const ACTIONS: Record<string, string> = {
  Created: 'Request created',
  Edited: 'Edited',
  Submitted: 'Sent to the Finance review',
  ApprovedByFinance: 'Finance approved',
  ReturnedByFinance: 'Finance returned it to the initiator',
  RejectedByFinance: 'Finance rejected it',
  Approved: 'The Procurement Manager approved',
  ReturnedByProcurement: 'The Procurement Manager returned it to the initiator',
  RejectedByProcurement: 'The Procurement Manager rejected it',
  CodeChanged: 'Item No. changed',
  CreatedInSap: 'Created in SAP',
  SapFailed: 'SAP refused',
  Retried: 'Creation in SAP retried',
  Cancelled: 'Cancelled',
};

const formatDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

interface Decision { step: 'finance' | 'procurement' | 'cancel'; action: 'approve' | 'return' | 'reject' | 'cancel'; title: string; confirm: string; commentRequired: boolean; help: string }

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value === '' || value === null || value === undefined ? '—' : value}</Typography>
    </Box>
  );
}

const ItemRequestDetail: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const requestId = Number(id);
  const [req, setReq] = useState<Detail | null>(null);
  const [lookups, setLookups] = useState<ItemRequestLookups | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<GLPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, GLOverride>>({});
  const [decision, setDecision] = useState<Decision | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res: any = await apiService.getItemRequest(requestId);
      setReq(res.data);
      setError(null);
      return res.data as Detail;
    } catch (e: any) {
      setError(errorMessage(e, 'The request could not be loaded'));
      return null;
    }
  }, [requestId]);

  useEffect(() => {
    if (!Number.isInteger(requestId) || requestId <= 0) { setError('Invalid request'); return; }
    load().then((d) => {
      if (!d) return;
      apiService.getItemRequestLookups(d.companyid).then((r: any) => setLookups(r.data)).catch(() => setLookups(null));
    });
  }, [requestId, load]);

  // The live G/L accounts while the request has not been through the Finance review,
  // and for the account names of the reviewed accounts afterwards.
  useEffect(() => {
    if (!req) return;
    let cancelled = false;
    setPreviewBusy(true);
    setPreviewError(null);
    apiService.getItemGLPreview({
      companyId: req.companyid,
      itemGroup: req.itemsgroupcode,
      itemClass: req.udfs?.U_Class || null,
      itemCode: req.sapitemcode || null,
      inventoryItem: req.inventoryitem,
    })
      .then((res: any) => { if (!cancelled) setPreview(res.data); })
      .catch((e: any) => { if (!cancelled) setPreviewError(errorMessage(e, 'The G/L accounts could not be read from SAP')); })
      .finally(() => { if (!cancelled) setPreviewBusy(false); });
    return () => { cancelled = true; };
  }, [req]);

  // After the Finance review, the table shows what Finance approved (with its item-only accounts).
  const reviewedPreview: GLPreview | null = useMemo(() => {
    if (!req?.glaccounts) return null;
    return {
      rows: req.glaccounts.reviewed.map((r) => ({
        ...r, warehouseType: r.warehouseType ?? null, warehouseCode: r.warehouseCode ?? null, warehouseCount: 0,
      })) as GLRow[],
      conditionalRules: 0,
      period: req.glaccounts.period,
      year: req.glaccounts.year,
      // The names kept at the review first: the live preview of a created item no longer
      // shows the accounts its own rules replaced.
      accountNames: { ...(preview?.accountNames || {}), ...(req.glaccounts.accountNames || {}) },
    };
  }, [req, preview]);
  const reviewedOverrides = useMemo(() => Object.fromEntries((req?.glaccounts?.overrides || []).map((o) => [o.key, o])), [req]);
  const reviewedKept = useMemo(() => req?.glaccounts?.itemRules?.filter((r) => r.kept).map((r) => r.key), [req]);

  const groupName = useMemo(() => lookups?.itemGroups.find((g) => g.code === req?.itemsgroupcode)?.name, [lookups, req]);
  const uomGroup = useMemo(() => lookups?.uomGroups.find((g) => g.entry === req?.uomgroupentry), [lookups, req]);

  const onOverrideChange = (row: GLRow, accounts: GLAccounts | null) => {
    setOverrides((o) => {
      const next = { ...o };
      if (accounts && Object.values(accounts).some(Boolean)) {
        next[row.key] = { key: row.key, label: row.label, warehouseType: row.warehouseType, warehouseCode: row.warehouseCode, accounts };
      } else {
        delete next[row.key];
      }
      return next;
    });
  };

  const act = async () => {
    if (!decision || !req) return;
    if (decision.commentRequired && !comment.trim()) { setActionError('Say why in the comment.'); return; }
    setBusy(true);
    setActionError(null);
    try {
      let res: any;
      if (decision.step === 'cancel') res = await apiService.cancelItemRequest(req.requestid, comment.trim() || undefined);
      else if (decision.step === 'finance') {
        res = await apiService.financeReviewItemRequest(req.requestid, {
          action: decision.action as 'approve' | 'return' | 'reject', comment: comment.trim() || undefined,
          overrides: decision.action === 'approve' ? Object.values(overrides) : undefined,
        });
      } else {
        res = await apiService.procurementDecideItemRequest(req.requestid, { action: decision.action as 'approve' | 'return' | 'reject', comment: comment.trim() || undefined });
      }
      setReq(res.data);
      setOverrides({});
      setDecision(null);
      setComment('');
      const s = ITEM_REQUEST_STATUS[res.data.status as Detail['status']];
      dispatch(showSnackbar({ message: `${res.data.requestno}: ${s?.label || res.data.status}`, severity: res.data.status === 'Failed' ? 'warning' : 'success' }));
    } catch (e: any) {
      setActionError(errorMessage(e, 'The action could not be recorded'));
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    if (!req) return;
    setBusy(true);
    try {
      const res: any = await apiService.retryItemRequest(req.requestid);
      setReq(res.data);
      dispatch(showSnackbar({ message: res.data.status === 'Created' ? `${res.data.requestno} was created in SAP` : `${res.data.requestno}: SAP refused it again`, severity: res.data.status === 'Created' ? 'success' : 'warning' }));
    } catch (e: any) {
      dispatch(showSnackbar({ message: errorMessage(e, 'The retry failed'), severity: 'error' }));
    } finally {
      setBusy(false);
    }
  };

  const open = (d: Decision) => { setComment(''); setActionError(null); setDecision(d); };

  if (error) return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="error">{error}</Alert></Box>;
  if (!req) return <Stack direction="row" spacing={1.5} sx={{ p: 3 }} role="status"><CircularProgress size={22} /><Typography>Loading…</Typography></Stack>;

  const status = ITEM_REQUEST_STATUS[req.status] || { label: req.status, color: 'default' as const };
  const p = req.permissions;
  const fieldMeta = new Map((lookups?.fields || []).map((f) => [f.field, f]));
  const udfRows = Object.entries(req.udfs || {}).filter(([, v]) => v);
  const valueLabel = (field: string, value: string) => {
    const v = fieldMeta.get(field)?.values.find((x) => x.value === value);
    return v && v.description && v.description !== v.value ? `${v.value} · ${v.description}` : value;
  };
  const glReviewed = !!req.glaccounts && !p.canFinanceReview;
  const keptKeysNow = preview ? keptWarehouseKeys(preview.rows, overrides) : [];
  const keptNow = (preview?.rows || []).filter((r) => keptKeysNow.includes(r.key)).map((r) => r.label);
  const plannedRules = req.glaccounts?.itemRules || [];
  const ruleText = (r: { label: string; kept?: boolean }) => `${r.label}${r.kept ? ' (keeps its own accounts)' : ''}`;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2.5, maxWidth: 1200 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <IconButton aria-label="Back to the item requests" onClick={() => navigate('/warehouse/item-requests')}><ArrowBackIcon /></IconButton>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5" component="h1">{req.requestno}</Typography>
            <Chip size="small" color={status.color} label={status.label} />
          </Stack>
          <Typography variant="body1">{req.itemname}</Typography>
          <Typography variant="body2" color="text.secondary">Requested by {req.createdbyname || 'unknown'} on {formatDate(req.createddate)}</Typography>
        </Box>
      </Stack>

      {req.status === 'Created' && (
        <Alert severity="success">Created in SAP as <b>{req.sapitemcode}</b> on {formatDate(req.createdinsapdate)}.</Alert>
      )}
      {req.status === 'Failed' && <Alert severity="error">{req.saperror || 'SAP refused the item.'}</Alert>}
      {req.status === 'Creating' && <Alert severity="info">PRPO is creating the item in SAP.</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        {p.canEdit && <Button variant="contained" onClick={() => navigate(`/warehouse/item-requests/${req.requestid}/edit`)}>Edit and send</Button>}
        {p.canFinanceReview && (
          <>
            <Button variant="contained" onClick={() => open({ step: 'finance', action: 'approve', title: 'Approve the G/L accounts', confirm: 'Approve', commentRequired: false,
              help: Object.keys(overrides).length
                ? `The request goes to the Procurement Manager with accounts for this item in ${Object.values(overrides).map((o) => o.label).join(', ')}.`
                  + (keptNow.length ? ` ${keptNow.join(', ')} ${keptNow.length === 1 ? 'keeps its' : 'keep their'} own accounts for this item.` : '')
                : 'The request goes to the Procurement Manager with the accounts of SAP’s rules.' })}>
              Approve for the Procurement Manager
            </Button>
            <Button variant="outlined" onClick={() => open({ step: 'finance', action: 'return', title: 'Return to the initiator', confirm: 'Return', commentRequired: true, help: 'The initiator can change the request and send it again.' })}>Return</Button>
            <Button color="error" onClick={() => open({ step: 'finance', action: 'reject', title: 'Reject the request', confirm: 'Reject', commentRequired: true, help: 'The request is closed.' })}>Reject</Button>
          </>
        )}
        {p.canProcurementApprove && (
          <>
            <Button variant="contained" onClick={() => open({ step: 'procurement', action: 'approve', title: 'Approve and create in SAP', confirm: 'Approve and create', commentRequired: false,
              help: `PRPO creates the item in SAP${req.glaccounts?.overrides?.length ? ', with the accounts Finance set for it' : ''}.` })}>
              Approve and create in SAP
            </Button>
            <Button variant="outlined" onClick={() => open({ step: 'procurement', action: 'return', title: 'Return to the initiator', confirm: 'Return', commentRequired: true, help: 'The initiator can change the request and send it again.' })}>Return</Button>
            <Button color="error" onClick={() => open({ step: 'procurement', action: 'reject', title: 'Reject the request', confirm: 'Reject', commentRequired: true, help: 'The request is closed.' })}>Reject</Button>
          </>
        )}
        {p.canRetry && <Button variant="contained" color="warning" onClick={retry} disabled={busy}>{busy ? 'Retrying…' : 'Retry the creation in SAP'}</Button>}
        {p.canCancel && <Button color="inherit" onClick={() => open({ step: 'cancel', action: 'cancel', title: 'Cancel the request', confirm: 'Cancel the request', commentRequired: false, help: 'The request is closed and nothing is created in SAP.' })}>Cancel request</Button>}
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>Item</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <Field label="Item No." value={<span style={{ fontFamily: 'monospace' }}>{req.sapitemcode || req.itemcode}</span>} />
          <Field label="Description" value={req.itemname} />
          <Field label="Foreign Name" value={req.foreignname} />
          <Field label="Item Group" value={groupName || req.itemsgroupcode} />
          <Field label="UoM Group" value={uomGroup ? uomGroup.code : req.uomgroupentry} />
          <Field label="Pricing Unit" value={uomGroup?.uoms.find((u) => u.entry === req.pricingunit)?.code || req.pricingunit} />
          <Field label="Item type" value={[req.inventoryitem && 'Inventory', req.salesitem && 'Sales', req.purchaseitem && 'Purchasing'].filter(Boolean).join(', ')} />
          <Field label="Active" value={req.active ? 'Yes' : 'No'} />
          <Field label="Bar Code" value={req.barcode} />
          {udfRows.map(([field, value]) => (
            <Field key={field} label={FIELD_LABELS[field] || lookups?.labels?.[field] || fieldMeta.get(field)?.description || field} value={valueLabel(field, String(value))} />
          ))}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>G/L accounts</Typography>
        {p.canFinanceReview && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Check the accounts SAP’s rules give this item. To use other accounts for this item only, edit a row: PRPO adds an SAP rule for the item when it is created.
          </Alert>
        )}
        {glReviewed ? (
          <GLDeterminationTable preview={reviewedPreview} overrides={reviewedOverrides} keptKeys={reviewedKept} />
        ) : (
          <GLDeterminationTable preview={preview} loading={previewBusy} error={previewError}
            editable={p.canFinanceReview} companyId={req.companyid} overrides={overrides} onOverrideChange={onOverrideChange} />
        )}
        {glReviewed && req.financebyname && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Reviewed by {req.financebyname} on {formatDate(req.financedate)}.
          </Typography>
        )}
        {(req.saprules || []).length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">Item rules in SAP</Typography>
            {(req.saprules || []).map((r) => (
              <Typography key={r.key} variant="body2" color={r.status === 'failed' ? 'error' : 'text.primary'}>
                {ruleText(r)}: {r.status === 'failed' ? `refused (${r.error})` : `rule ${r.absoluteEntry ?? ''} ${r.status === 'existing' ? 'already there' : 'created'}`}
              </Typography>
            ))}
          </Box>
        ) : plannedRules.length > 0 && ['PendingProcurement', 'Creating', 'Failed'].includes(req.status) && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">Item rules PRPO adds in SAP</Typography>
            {plannedRules.map((r) => <Typography key={r.key} variant="body2">{ruleText(r)}</Typography>)}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>History</Typography>
        <Stack divider={<Divider flexItem />} spacing={1}>
          {req.history.map((h) => (
            <Box key={h.historyid}>
              <Typography variant="body2"><b>{ACTIONS[h.action] || h.action}</b>{h.username ? ` · ${h.username}` : ''}</Typography>
              <Typography variant="caption" color="text.secondary">{formatDate(h.createddate)}</Typography>
              {h.comment && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{h.comment}</Typography>}
            </Box>
          ))}
        </Stack>
      </Paper>

      <ResponsiveDialog open={!!decision} onClose={() => !busy && setDecision(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{decision?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{decision?.help}</Typography>
          <TextField fullWidth multiline minRows={3} id="item-request-comment" label={decision?.commentRequired ? 'Comment (required)' : 'Comment (optional)'}
            value={comment} onChange={(e) => setComment(e.target.value)} />
          {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecision(null)} disabled={busy}>Back</Button>
          <Button variant="contained" color={decision?.action === 'reject' ? 'error' : 'primary'} onClick={act} disabled={busy}>
            {busy ? 'Working…' : decision?.confirm}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default ItemRequestDetail;
